import {
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import { DebouncedInput } from 'components/input';
import Loading from 'components/Loading';
import { Icon } from 'components/icon';
import { Button, Message } from 'modules';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

import { IndeterminateCheckbox } from './Components';
import { initFilterFunction, itemsPerPageOptions } from './config';
import type { TableListDataProps } from './types';
import { useColumnsDefine, usePagination } from './utils';
import { DEFAULT_PAGE_INDEX } from 'configs';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from 'components';
import { useTableRowFocus } from '../useTableRowFocus';

const TableListData = <TData extends RowData>(
  props: TableListDataProps<TData>,
) => {
  const {
    columns: columnDefined,
    useQuery,
    variables,
    refetchDataCurrentPage,
    setVariables,
    convertVariables,
    filterKey,
    disableNavigation,
    callBackData,
    initialSelectedRows,
    clearSelectedRowsKey,
    ...tProps
  } = props;

  const [rowSelection, setRowSelection] = useState({});
  const [hasInitialized, setHasInitialized] = useState(false);
  const callbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const [previousSelectedIds, setPreviousSelectedIds] = useState<Set<string>>(
    new Set(),
  );
  const [previousDataTotal, setPreviousDataTotal] = useState<number>(0);
  const [previousDataKey, setPreviousDataKey] = useState<string>('');

  const { pagination, setPagination } = usePagination({
    initPageIndex: variables?.pageIndex,
    initPageSize: variables?.pageSize,
  });

  const [fetchData, setFetchData] = useState<number>(0);
  const [appliedVariables, setAppliedVariables] = useState(variables);
  const [previousPageIndex, setPreviousPageIndex] = useState<number>(
    pagination.pageIndex,
  );

  const navigate = useNavigate();
  const location = useLocation();

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear any pending callback on unmount
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
        callbackTimeoutRef.current = null;
      }
    };
  }, []);

  const memoVariables = useMemo(() => {
    const filter = {
      ...(convertVariables
        ? convertVariables({ ...appliedVariables, ...pagination })
        : appliedVariables),
      ...(filterKey && {
        [filterKey as unknown as string]: appliedVariables?.searchDetailContent,
      }),
      ...pagination,
      fetchData: fetchData,
    };

    if (fetchData != 0) {
      return initFilterFunction(filter)();
    } else {
      return {};
    }
  }, [fetchData, appliedVariables, pagination]);

  useEffect(() => {
    if (fetchData !== 0 && !disableNavigation) {
      const filter = {
        ...(convertVariables
          ? convertVariables({ ...appliedVariables, ...pagination })
          : appliedVariables),
        ...(filterKey && {
          [filterKey as unknown as string]:
            appliedVariables?.searchDetailContent,
        }),
        ...pagination,
        fetchData: fetchData,
      };

      const lstMap = Object.entries(filter).map(([key, value]) => {
        return `${encodeURIComponent(key)}=${encodeURIComponent(
          value?.toString() ?? '',
        )}`;
      });

      navigate(`${location.pathname}?${lstMap.join('&')}`);
    }
  }, [memoVariables, disableNavigation]);

  const { isFetching, data = '', refetch } = useQuery?.({ ...memoVariables });

  useEffect(() => {
    if (!variables) return;
    setAppliedVariables(variables);
    setPagination({
      pageIndex: fetchData == 0 ? variables?.pageIndex : DEFAULT_PAGE_INDEX,
      pageSize: pagination.pageSize,
    });
    setFetchData((prev) => prev + 1);
    // !!refetch && refetch();
  }, [variables]);

  useEffect(() => {
    if (!refetchDataCurrentPage) return;
    setFetchData((prev) => prev + 1);
  }, [refetchDataCurrentPage]);

  useEffect(() => {
    if (clearSelectedRowsKey === undefined) return;
    setRowSelection({});
    setPreviousSelectedIds(new Set());

    if (callbackTimeoutRef.current) {
      clearTimeout(callbackTimeoutRef.current);
      callbackTimeoutRef.current = null;
    }
  }, [clearSelectedRowsKey]);

  useEffect(() => {
    if (data == '') return;
    // return
    if (callBackData) {
      callBackData(data);
    }
  }, [data]);

  // const memoData = useMemo(() => (Array.isArray(data) ? data : data?.data || data?.[tProps?.dataQueryKey] || []), [data]);
  const memoData = useMemo(
    () =>
      (Array.isArray(data)
        ? data
        : tProps?.dataQueryKey
          ? data?.data?.[tProps?.dataQueryKey]
          : data?.data) ?? [],
    [data],
  );

  const getRowFocusProps = useTableRowFocus(memoData);

  const totalRows = useMemo(() => {
    const totalCandidates = [
      data?.total,
      data?.totalCount,
      data?.totalRecords,
      data?.count,
      data?.data?.total,
    ];

    const validTotal = totalCandidates.find((total) => {
      const numericTotal = Number(total);
      return Number.isFinite(numericTotal);
    });

    return validTotal !== undefined ? Number(validTotal) : memoData.length;
  }, [data, memoData.length]);

  const pageCount = useMemo(() => {
    const calculatedPageCount =
      pagination.pageSize > 0 ? Math.ceil(totalRows / pagination.pageSize) : 0;

    return Math.max(
      calculatedPageCount,
      pagination.pageIndex + 1,
      memoData.length ? 1 : 0,
    );
  }, [memoData.length, pagination.pageIndex, pagination.pageSize, totalRows]);

  // Reset row selection when data source changes completely
  useEffect(() => {
    const currentDataTotal = totalRows || 0;
    const currentDataKey = JSON.stringify(
      convertVariables ? convertVariables(variables) : variables,
    );

    // Check if this is a completely new data set (different query parameters or total count changed significantly)
    const isNewDataSet =
      previousDataTotal > 0 &&
      currentDataTotal !== previousDataTotal &&
      previousDataKey !== currentDataKey;

    if (isNewDataSet) {
      // Reset selections for completely new data
      setRowSelection({});
      setPreviousSelectedIds(new Set());
      setHasInitialized(false); // Allow re-initialization with new data
    }

    // Update tracking values
    setPreviousDataTotal(currentDataTotal);
    setPreviousDataKey(currentDataKey);

    // Clear pending callbacks
    if (callbackTimeoutRef.current) {
      clearTimeout(callbackTimeoutRef.current);
      callbackTimeoutRef.current = null;
    }
  }, [data, variables, convertVariables, totalRows]);

  // Re-validate row selection when page changes
  useEffect(() => {
    if (previousPageIndex !== pagination.pageIndex && memoData.length > 0) {
      // Re-validate current selections with new page data
      const validatedSelection: Record<string, boolean> = {};

      // Get currently selected row IDs from previous selections
      const currentlySelectedIds = Array.from(previousSelectedIds);

      // Check each row in new page data
      memoData.forEach((row: any, index: number) => {
        const rowId = String(row.id);
        const wasSelected = currentlySelectedIds.includes(rowId);

        if (wasSelected) {
          // Check if this row can still be selected
          const canSelect = tProps?.canSelectable
            ? Boolean(tProps?.canSelectable?.(row))
            : true;

          if (canSelect) {
            validatedSelection[index] = true;
          } else {
            // Remove from previousSelectedIds if no longer selectable
            setPreviousSelectedIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(rowId);
              return newSet;
            });
          }
        }
      });

      // Update selection state with validated selections
      setRowSelection(validatedSelection);
      setPreviousPageIndex(pagination.pageIndex);

      // Clear pending callbacks
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
        callbackTimeoutRef.current = null;
      }
    }
  }, [
    pagination.pageIndex,
    previousPageIndex,
    memoData,
    tProps?.canSelectable,
    previousSelectedIds,
  ]);

  // Update row selection when data changes (for filtering) to maintain selections
  useEffect(() => {
    // Only initialize if we haven't done so yet and we have initial rows
    if (
      !hasInitialized &&
      initialSelectedRows &&
      initialSelectedRows.length > 0 &&
      memoData.length > 0
    ) {
      const updatedSelection: Record<string, boolean> = {};

      // Find matching rows in current filtered data and mark them as selected
      memoData.forEach((row: any, index: number) => {
        const isSelected = initialSelectedRows.some(
          (selectedRow: any) => String(selectedRow.id) === String(row.id),
        );
        if (isSelected) {
          updatedSelection[index] = true;
        }
      });

      // Update selection to match current data
      setRowSelection(updatedSelection);

      // Initialize previousSelectedIds on first run only
      const initialIds = new Set(
        initialSelectedRows.map((row: any) => String(row.id)),
      );
      setPreviousSelectedIds(initialIds);
      setHasInitialized(true);
    } else if (
      !hasInitialized &&
      (!initialSelectedRows || initialSelectedRows.length === 0)
    ) {
      // Clear selection if no initial rows provided - only on first init
      setRowSelection({});
      setPreviousSelectedIds(new Set());
      setHasInitialized(true);
    }
  }, [memoData, initialSelectedRows, hasInitialized]);

  const { columns, ...rest } = useColumnsDefine({
    columns: columnDefined,
    ...tProps,
  });

  const memoColumns = useMemo(() => columns, [columns]);

  const table = useReactTable({
    data: memoData,
    columns: memoColumns,
    state: {
      rowSelection,
      pagination,
    },
    pageCount,
    enableRowSelection: tProps?.canSelectable
      ? (row) => Boolean(tProps?.canSelectable?.(row.original))
      : rest?.selectable,
    onPaginationChange: setPagination,
    onRowSelectionChange: (updater) => {
      setRowSelection(updater);

      // Handle callback after state update
      if (tProps.onSelectedRowsCallback && isMountedRef.current) {
        // Clear any pending timeout first
        if (callbackTimeoutRef.current) {
          clearTimeout(callbackTimeoutRef.current);
          callbackTimeoutRef.current = null;
        }

        // Set a timeout to call callback after selection state is updated
        callbackTimeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;

          // Get updated selection after state change
          const newSelection =
            typeof updater === 'function' ? updater(rowSelection) : updater;

          const currentSelectedRows: any[] = [];
          Object.keys(newSelection).forEach((indexStr) => {
            const index = parseInt(indexStr);
            if (newSelection[index] && memoData[index]) {
              currentSelectedRows.push(memoData[index]);
            }
          });

          const currentSelectedIds = new Set(
            currentSelectedRows.map((row: any) => String(row.id)),
          );

          // Find newly selected (added)
          const addedRows = currentSelectedRows.filter(
            (row: any) => !previousSelectedIds.has(String(row.id)),
          );

          // Find unselected (removed) - only if the row still exists in current memoData
          const removedIds = Array.from(previousSelectedIds).filter((id) => {
            return (
              !currentSelectedIds.has(id) &&
              memoData.some((row: any) => String(row.id) === id)
            );
          });

          // Call callback for each action
          addedRows.forEach((row) => {
            if (tProps.onSelectedRowsCallback && isMountedRef.current) {
              tProps.onSelectedRowsCallback({
                action: 'select',
                row: row,
                allSelected: currentSelectedRows,
              });
            }
          });

          // For removed, find the row data from memoData
          removedIds.forEach((id) => {
            const removedRow = memoData.find(
              (row: any) => String(row.id) === id,
            );
            if (
              removedRow &&
              tProps.onSelectedRowsCallback &&
              isMountedRef.current
            ) {
              tProps.onSelectedRowsCallback({
                action: 'unselect',
                row: removedRow,
                allSelected: currentSelectedRows,
              });
            }
          });

          // Update previous selected IDs for next comparison
          setPreviousSelectedIds(currentSelectedIds);
          callbackTimeoutRef.current = null;
        }, 50); // Slightly longer timeout to ensure state is fully updated
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    debugTable: tProps?.debugTable,
  });

  const renderCellContent = useCallback((cell: any) => {
    const renderedContent = flexRender(
      cell.column.columnDef.cell,
      cell.getContext(),
    );
    const rawValue = cell.getValue?.();

    const isTextValue =
      (typeof rawValue === 'string' || typeof rawValue === 'number') &&
      String(rawValue).trim() !== '';

    if (!isTextValue) {
      return renderedContent;
    }

    const text = String(rawValue);

    return (
      <span className='Table__CellText' title={text}>
        {renderedContent}
      </span>
    );
  }, []);

  const getColumnSizeStyle = useCallback((columnDef: any) => {
    const columnSize = columnDef?.meta?.columnSize;

    if (!columnSize) return undefined;

    const style: React.CSSProperties = {
      boxSizing: 'border-box',
      width: columnSize.width,
      minWidth: columnSize.minWidth,
      maxWidth: columnSize.maxWidth,
    };

    return style;
  }, []);

  return (
    <Card className='container-fluid'>
      <Loading isLoading={isFetching}>
        <div className='Table flat'>
          <div
            className='Header d-flex justify-content-end align-items-center g10'
            style={{
              position: 'sticky',
              top: '0',
              zIndex: 1,
              background: 'transparent',
              borderBottom: 'none',
              padding: '0 0 16px 0',
            }}
          >
            {typeof tProps?.customHeader === 'function' ? (
              tProps?.customHeader(data)
            ) : (
              <></>
            )}

            {filterKey ? (
              <div className='TableFilterInputText'>
                <DebouncedInput
                  value={variables?.searchDetailContent ?? ''}
                  onChange={(value) => {
                    if (fetchData > 0) {
                      setVariables &&
                        setVariables((prev: any) => ({
                          ...prev,
                          searchDetailContent: String(value)?.trim(),
                        }));
                    }
                  }}
                  placeholder='Nhập từ khóa tìm kiếm'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setVariables &&
                        setVariables((prev: any) => ({
                          ...prev,
                          searchDetailContent: String(e.target.value)?.trim(),
                        }));
                    }
                  }}
                />
              </div>
            ) : (
              <></>
            )}

            {typeof tProps?.customHeaderBtnGroup === 'function' ? (
              tProps?.customHeaderBtnGroup(data)
            ) : (
              <></>
            )}

            {tProps?.hasRefetch && (
              <Button
                type='button'
                buttonType='info-outline'
                className='mr0 ml5 mt24 mb24 pdr10 pdl10'
                onClick={() => refetch?.()}
                icon={() => <Icon.Reload />}
                label={false}
              />
            )}
          </div>

          <div className='Table__Wrapper'>
            <table>
              <thead className='Table__Head'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={getColumnSizeStyle(header.column.columnDef)}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              <tbody className='Table__Body'>
                {typeof tProps?.customTopRow === 'function' ? (
                  tProps?.customTopRow(data)
                ) : (
                  <></>
                )}

                {memoData.length ? (
                  table.getRowModel().rows.map((row) => {
                    const isDisabledSelectableRow = Boolean(
                      rest?.selectable &&
                      tProps?.canSelectable &&
                      !tProps.canSelectable(row.original),
                    );

                    return (
                      <tr key={row.id} {...getRowFocusProps(row.original)}>
                        {row.getVisibleCells().map((cell, index) => {
                          const isSelectCell = cell.column.id === 'select';

                          return (
                            <td
                              key={index}
                              style={getColumnSizeStyle(cell.column.columnDef)}
                              className={
                                isDisabledSelectableRow && isSelectCell
                                  ? 'Table__Checkbox--disabled'
                                  : undefined
                              }
                              aria-disabled={
                                isDisabledSelectableRow && isSelectCell
                              }
                            >
                              {renderCellContent(cell)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={20}>
                      <Message
                        type='emptyData'
                        message='Không tìm thấy dữ liệu'
                      />
                    </td>
                  </tr>
                )}
                {typeof tProps?.customBottomRow === 'function' ? (
                  tProps?.customBottomRow(data)
                ) : (
                  <></>
                )}
              </tbody>

              {memoData.length && rest?.selectable ? (
                <tfoot>
                  <tr>
                    <td>
                      <IndeterminateCheckbox
                        {...{
                          checked: table.getIsAllPageRowsSelected(),
                          indeterminate: table.getIsSomePageRowsSelected(),
                          onChange: table.getToggleAllPageRowsSelectedHandler(),
                        }}
                      />
                    </td>
                    <td colSpan={20}>
                      Tổng: ({totalRows || table.getRowModel().rows.length})
                    </td>
                  </tr>
                </tfoot>
              ) : (
                <></>
              )}
            </table>
          </div>

          {memoData.length ? (
            <div
              className='Footer'
              style={{ position: 'sticky', bottom: '0', left: '0', zIndex: 1 }}
            >
              <div className='PaginationInfo'>
                {rest?.selectable ? (
                  <>
                    {`${Object.keys(rowSelection).length} / ${totalRows || table.getPreFilteredRowModel().rows.length} đã chọn`}

                    {typeof tProps?.customSelectedFooter === 'function' ? (
                      tProps?.customSelectedFooter(data)
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  `Tổng: ${totalRows || table.getPreFilteredRowModel().rows.length}`
                )}
              </div>

              <div
                className='d-flex align-items-center'
                {...{
                  style: {
                    textAlign: 'right',
                  },
                }}
              >
                <div className='Pagination'>
                  <button
                    type='button'
                    className='btnPagination prev'
                    onClick={() => {
                      table.previousPage();
                      setFetchData((prev) => prev + 1);
                    }}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <Icon.Prev />
                  </button>

                  <strong>
                    {Number.isFinite(table.getPageCount()) &&
                    Number.isFinite(table.getState().pagination.pageIndex)
                      ? `${table.getState().pagination.pageIndex + 1} / ${table.getPageCount()}`
                      : ''}
                  </strong>

                  <button
                    type='button'
                    className='btnPagination next'
                    onClick={() => {
                      table.nextPage();
                      setFetchData((prev) => prev + 1);
                    }}
                    disabled={!table.getCanNextPage()}
                  >
                    <Icon.Next />
                  </button>
                </div>

                <div className='Pagination'>
                  <select
                    title='Page size'
                    className='pageSize'
                    name='pageSize'
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                      table.setPageSize(Number(e.target.value));
                      setFetchData((prev) => prev + 1);
                    }}
                  >
                    {itemsPerPageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {typeof tProps?.customFooter === 'function' ? (
                tProps?.customFooter(data)
              ) : (
                <></>
              )}
            </div>
          ) : (
            <></>
          )}
        </div>
      </Loading>
    </Card>
  );
};

export default TableListData;
